import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ExpressionCamera from "@/components/ExpressionCamera";
import api, { formatApiErrorDetail } from "@/lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2, Sparkles, Clock } from "lucide-react";
import { toast } from "sonner";
import { NEPBadge } from "@/lib/nep";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { CATEGORY_META } from "@/lib/categoryMeta";

const MOUSE_SAMPLE_THROTTLE_MS = 100;
const MOUSE_SAMPLE_CAP = 300;

export default function Assessment() {
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const { lang } = useLang();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({}); // qid -> selected_index
  const [i, setI] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");

  // Behavioral telemetry — accumulated per question_id, sent only on final submit.
  const visitStartRef = useRef(Date.now());
  const timeSpentRef = useRef({}); // qid -> ms
  const changeCountRef = useRef({}); // qid -> count
  const mousePathRef = useRef({}); // qid -> [{x,y,t}]
  const lastSampleRef = useRef(0);
  const cardRef = useRef(null);
  const expressionsRef = useRef({}); // qid -> [{expression, confidence}]
  const currentQidRef = useRef(null);

  // Visible session clock — starts on the first question, stops once submitted.
  const sessionStartRef = useRef(null);
  const [elapsedMs, setElapsedMs] = useState(0);

  const flushTime = (qid) => {
    if (!qid) return;
    timeSpentRef.current[qid] = (timeSpentRef.current[qid] || 0) + (Date.now() - visitStartRef.current);
    visitStartRef.current = Date.now();
  };

  const recordMovePoint = (clientX, clientY) => {
    if (!q || !cardRef.current) return;
    const now = Date.now();
    if (now - lastSampleRef.current < MOUSE_SAMPLE_THROTTLE_MS) return;
    const path = mousePathRef.current[q.id] || (mousePathRef.current[q.id] = []);
    if (path.length >= MOUSE_SAMPLE_CAP) return;
    lastSampleRef.current = now;
    const rect = cardRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    path.push({
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
      t: now - visitStartRef.current,
    });
  };

  const handleMouseMove = (e) => recordMovePoint(e.clientX, e.clientY);
  const handleTouchMove = (e) => {
    const touch = e.touches?.[0];
    if (touch) recordMovePoint(touch.clientX, touch.clientY);
  };

  useEffect(() => {
    if (!user) return;
    if (!user.has_paid) {
      nav("/pay", { replace: true });
    } else if (!(user.grade && user.education_board && user.school_name)) {
      nav("/register", { replace: true });
    }
  }, [user, nav]);

  useEffect(() => {
    if (!user?.has_paid || !(user.grade && user.education_board && user.school_name)) return;
    Promise.all([
      api.get("/questions"),
      api.get("/assessment/progress").catch(() => ({ data: { answers: {} } })),
    ]).then(([qRes, pRes]) => {
      // shuffle categories so it feels varied but grouped
      const order = ["personality", "interest", "aptitude", "mental_ability"];
      const sorted = [...qRes.data].sort((a, b) => order.indexOf(a.category) - order.indexOf(b.category));
      setQuestions(sorted);

      const restored = pRes.data?.answers || {};
      setAnswers(restored);
      const firstUnanswered = sorted.findIndex((qq) => !(qq.id in restored));
      const resumeAt = firstUnanswered === -1 ? Math.max(0, sorted.length - 1) : firstUnanswered;
      setI(resumeAt);
      if (resumeAt > 0) toast.info("Resuming where you left off.");
    }).catch((e) => {
      const msg = formatApiErrorDetail(e.response?.data?.detail) || e.message || "Failed to load questions.";
      if (msg.toLowerCase().includes("complete your profile")) {
        nav("/register", { replace: true });
        return;
      }
      setLoadError(msg);
    }).finally(() => setLoading(false));
  }, [user, nav]);

  const q = questions[i];
  const total = questions.length;
  const currentIds = useMemo(() => new Set(questions.map((qq) => qq.id)), [questions]);
  const answeredCount = useMemo(
    () => Object.keys(answers).filter((qid) => currentIds.has(qid)).length,
    [answers, currentIds]
  );
  const progress = total ? Math.round((answeredCount / total) * 100) : 0;
  const selected = q ? answers[q.id] : undefined;

  useEffect(() => {
    if (!q) return;
    visitStartRef.current = Date.now();
    currentQidRef.current = q.id;
  }, [q?.id]);

  const recordExpression = (expression, confidence) => {
    const qid = currentQidRef.current;
    if (!qid) return;
    const list = expressionsRef.current[qid] || (expressionsRef.current[qid] = []);
    list.push({ expression, confidence: Math.round(confidence * 100) / 100 });
  };

  // Live session clock: starts the moment the first question renders, ticks every second
  // until the assessment is submitted (or the page is left).
  useEffect(() => {
    if (!q || submitting) return;
    if (!sessionStartRef.current) sessionStartRef.current = Date.now();
    const tick = () => setElapsedMs(Date.now() - sessionStartRef.current);
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [!!q, submitting]);

  const formatClock = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const mm = String(m).padStart(2, "0");
    const ss = String(s).padStart(2, "0");
    return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
  };

  // Localized text + options based on current language
  const localized = (() => {
    if (!q) return { text: "", options: [] };
    const tr = q.translations && q.translations[lang];
    if (tr && tr.text && Array.isArray(tr.options) && tr.options.length === q.options.length) {
      return { text: tr.text, options: tr.options };
    }
    return { text: q.text, options: q.options };
  })();

  const select = (idx) => {
    if (!q) return;
    if (answers[q.id] !== undefined && answers[q.id] !== idx) {
      changeCountRef.current[q.id] = (changeCountRef.current[q.id] || 0) + 1;
    }
    const next = { ...answers, [q.id]: idx };
    setAnswers(next);
    const payload = { answers: Object.entries(next).map(([question_id, selected_index]) => ({ question_id, selected_index })) };
    api.put("/assessment/progress", payload).catch(() => {});
  };

  const canGoNext = useMemo(() => selected !== undefined && i < total - 1, [selected, i, total]);
  const canSubmit = useMemo(() => answeredCount === total && total > 0, [answeredCount, total]);

  const buildTelemetry = () => {
    const totalTimeMs = Object.values(timeSpentRef.current).reduce((sum, ms) => sum + ms, 0);
    return {
      total_time_ms: Math.round(totalTimeMs),
      questions: Object.keys(answers).filter((qid) => currentIds.has(qid)).map((qid) => ({
        question_id: qid,
        time_spent_ms: Math.round(timeSpentRef.current[qid] || 0),
        change_count: changeCountRef.current[qid] || 0,
        mouse_path: mousePathRef.current[qid] || [],
        expressions: expressionsRef.current[qid] || [],
      })),
    };
  };

  const goPrev = () => {
    flushTime(q?.id);
    setI((x) => Math.max(0, x - 1));
  };

  const goNext = () => {
    flushTime(q?.id);
    setI((x) => Math.min(total - 1, x + 1));
  };

  const submit = async () => {
    flushTime(q?.id);
    setSubmitting(true);
    try {
      const payload = {
        answers: Object.entries(answers)
          .filter(([question_id]) => currentIds.has(question_id))
          .map(([question_id, selected_index]) => ({ question_id, selected_index })),
        language: lang,
        telemetry: buildTelemetry(),
      };
      const { data } = await api.post("/assessment/submit", payload);
      await refreshUser();
      toast.success("Your AI report is ready!");
      nav(`/report/${data.id}`);
    } catch (e) {
      toast.error("Failed to submit. Please try again.");
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-20 label-mono">Loading questions…</div>
      </div>
    );
  }

  if (!q) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-20">
          <div className="card-brutal p-8 bg-red-50">
            <p className="label-mono">Couldn't load the assessment</p>
            <p className="text-sm mt-2 text-[#52525B]">{loadError || "No questions are available right now."}</p>
            <button onClick={() => nav("/dashboard")} className="btn-brutal bg-white mt-6 px-5 py-3">
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
  const meta = CATEGORY_META[q.category] || { label: q.category, color: "bg-white" };

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
        {/* NEP + grade banner */}
        <div className="mb-6 flex flex-wrap items-center gap-3">
          <NEPBadge grade={user?.grade} />
          <span className="label-mono text-[#52525B]">Class {user?.grade || "—"} · {user?.education_board || "Board N/A"}</span>
          <span
            data-testid="assessment-clock"
            className="ml-auto label-mono flex items-center gap-1.5 px-3 py-1 border-2 border-[#0A0A0A] rounded-full bg-white"
          >
            <Clock size={14} strokeWidth={2.5} /> {formatClock(elapsedMs)}
          </span>
        </div>

        <ExpressionCamera onSample={recordExpression} active={!submitting} />

        {/* Progress */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="label-mono">Question {i + 1} of {total}</span>
            <span data-testid="assessment-progress" className="label-mono">{progress}% complete</span>
          </div>
          <div className="h-3 w-full bg-white border-2 border-[#0A0A0A] rounded-full overflow-hidden">
            <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={q.id}
            ref={cardRef}
            onMouseMove={handleMouseMove}
            onTouchMove={handleTouchMove}
            initial={{ x: 24, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -24, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className={`card-brutal p-8 ${meta.color}`}
          >
            <span className="label-mono">{meta.label}</span>
            <h2 data-testid="question-text" className="font-display text-2xl md:text-3xl font-extrabold tracking-tight mt-3 leading-snug">
              {localized.text}
            </h2>

            <div className="mt-8 grid grid-cols-1 gap-3">
              {localized.options.map((opt, idx) => {
                const isSel = selected === idx;
                return (
                  <button
                    key={idx}
                    data-testid={`option-${idx}`}
                    onClick={() => select(idx)}
                    className={`text-left w-full border-2 border-[#0A0A0A] rounded-xl px-5 py-4 font-medium transition-transform ${
                      isSel ? "bg-blue-600 text-white translate-x-1" : "bg-white hover:-translate-y-0.5"
                    }`}
                  >
                    <span className="label-mono opacity-70 mr-3">{String.fromCharCode(65 + idx)}</span>
                    {opt}
                  </button>
                );
              })}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Nav */}
        <div className="mt-8 flex items-center justify-between">
          <button
            data-testid="prev-btn"
            onClick={goPrev}
            disabled={i === 0}
            className="btn-brutal bg-white px-5 py-3 flex items-center gap-2 disabled:opacity-40"
          >
            <ArrowLeft size={16} strokeWidth={2.5} /> Previous
          </button>

          {i < total - 1 ? (
            <button
              data-testid="next-btn"
              onClick={goNext}
              disabled={selected === undefined}
              className="btn-brutal bg-[#0A0A0A] text-white px-5 py-3 flex items-center gap-2 disabled:opacity-40"
            >
              Next <ArrowRight size={16} strokeWidth={2.5} />
            </button>
          ) : (
            <button
              data-testid="submit-assessment-btn"
              onClick={submit}
              disabled={!canSubmit || submitting}
              className="btn-brutal bg-blue-600 text-white px-6 py-3 flex items-center gap-2 disabled:opacity-60"
            >
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <Sparkles size={16} strokeWidth={2.5} />}
              {submitting ? "AI is analyzing…" : "Get AI Report"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
